import React, { useState, useEffect } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import { useUser } from 'contexts/UserContext';
// Custom components
import BankReconciliation from '../utility/components/BankReconciliation';

// Assets (your image imports remain the same)
import img1 from 'assets/img/nfts/profitloss.svg';
import img2 from 'assets/img/nfts/balance sheet.svg';
import img3 from 'assets/img/nfts/contacts payable.svg';
import img03 from 'assets/img/nfts/statement.svg';
import img4 from 'assets/img/nfts/expenses.svg';
import img5 from 'assets/img/nfts/income.svg';
import img6 from 'assets/img/nfts/purchase detail.svg';
import img7 from 'assets/img/nfts/purchase summary.svg';
import img8 from 'assets/img/nfts/sale detail.svg';
import img9 from 'assets/img/nfts/sale summary.svg';
import img10 from 'assets/img/nfts/item by contact.svg';
import img11 from 'assets/img/nfts/stock.svg';
import img12 from 'assets/img/nfts/retail.svg';
import img13 from 'assets/img/nfts/tds report.svg';
import img14 from 'assets/img/nfts/gstr1.svg';
import img15 from 'assets/img/nfts/gstr3b.svg';
import img16 from 'assets/img/nfts/Item + Serialwise History.svg';
import img17 from 'assets/img/nfts/Serial Wise Bill To Bill Profit.svg';
import img18 from 'assets/img/nfts/Serial Stock.svg';
import img19 from 'assets/img/nfts/Party Stock.svg';
import { useGlobal } from 'contexts';

// Skeleton Loader Components
const ReportCardSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const cardBg = isDark ? '#2d3748' : '#ffffff';
   const borderColor = isDark ? '#4a5568' : '#e2e8f0';

   return (
      <div
         className="group rounded-2xl overflow-hidden transition-all duration-300 border-2"
         style={{
            backgroundColor: cardBg,
            borderColor: borderColor,
         }}>
         <div className="p-6">
            {/* Header */}
            <div className="flex items-start mb-4">
               <div
                  className="w-14 h-14 rounded-xl border-2 mr-4"
                  style={{
                     backgroundColor: isDark ? '#4a5568' : '#f7fafc',
                     borderColor: borderColor,
                  }}>
                  <div className={`w-full h-full rounded-xl ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
               </div>
               <div className="flex-1">
                  <div
                     className={`h-6 rounded mb-2 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
                     style={{ width: '80%' }}
                  />
                  <div className={`h-4 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} style={{ width: '90%' }} />
                  <div
                     className={`h-4 rounded mt-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
                     style={{ width: '70%' }}
                  />
               </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${borderColor}` }}>
               <div>
                  <div className={`h-3 w-12 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
               </div>
               <div className={`w-5 h-5 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
            </div>
         </div>

         {/* Hover Effect Bar */}
         <div className={`h-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
      </div>
   );
};

const TabSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const cardBg = isDark ? '#2d3748' : '#ffffff';
   const borderColor = isDark ? '#4a5568' : '#e2e8f0';

   return (
      <div
         className="rounded-2xl p-2 mb-6"
         style={{
            backgroundColor: cardBg,
            border: `1px solid ${borderColor}`,
         }}>
         <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
               <div
                  key={index}
                  className="px-4 py-3 rounded-xl border-2"
                  style={{
                     borderColor: borderColor,
                  }}>
                  <div className={`h-6 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
               </div>
            ))}
         </div>
      </div>
   );
};

const HeaderSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <div className="flex mb-3">
         <div className="flex-1">
            <div className={`h-8 rounded mb-2 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} style={{ width: '250px' }} />
            <div className={`h-4 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} style={{ width: '300px' }} />
         </div>
      </div>
   );
};

export default function Marketplace() {
   const { colorMode } = useColorMode();
   const { user } = useUser();
   const isDark = colorMode === 'dark';
   const navigate = useNavigate();
   const { company } = useGlobal();
   const [isRenumberingTransection, setIsRenumberingTransection] = useState(false);
   const [isMobileBusiness, setIsMobileBusiness] = useState(false);
   const [activeTab, setActiveTab] = useState('accounting'); // Default value
   const [searchTerm, setSearchTerm] = useState('');
   const [isInitialized, setIsInitialized] = useState(false);
   const [isLoading, setIsLoading] = useState(true);

   const applyGST = company?.setting?.applyGST;
   const gstRegNo = company?.gstRegNo;

   // Check if GST reports should be shown
   const shouldShowGSTReports = applyGST && gstRegNo;

   // Initialize all states from localStorage
   useEffect(() => {
      const initializeFromStorage = () => {
         try {
            // Initialize active tab
            const savedTab = localStorage.getItem('activeReportTab');
            if (savedTab) {
               setActiveTab(savedTab);
            }

            // Initialize business type
            const businessType = localStorage.getItem('businessTypes');
            setIsMobileBusiness(businessType === 'MOBILE' || businessType === 'mobile');

            setIsInitialized(true);
            setIsLoading(false);
         } catch (error) {
            console.error('Error reading localStorage:', error);
            setIsInitialized(true);
            setIsLoading(false);
         }
      };

      initializeFromStorage();
   }, []);

   // Save active tab to localStorage whenever it changes
   useEffect(() => {
      if (isInitialized && typeof window !== 'undefined') {
         localStorage.setItem('activeReportTab', activeTab);
      }
   }, [activeTab, isInitialized]);

   // Listen for business type changes
   useEffect(() => {
      if (!isInitialized) return;

      const handleStorageChange = (e) => {
         if (e.key === 'businessTypes') {
            setIsMobileBusiness(e.newValue === 'MOBILE' || e.newValue === 'mobile');
         }
      };

      const handleVisibilityChange = () => {
         if (!document.hidden) {
            try {
               const businessType = localStorage.getItem('businessTypes');
               setIsMobileBusiness(businessType === 'MOBILE' || businessType === 'mobile');
            } catch (error) {
               console.error('Error updating businessType:', error);
            }
         }
      };

      window.addEventListener('storage', handleStorageChange);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
         window.removeEventListener('storage', handleStorageChange);
         document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
   }, [isInitialized]);

   const handleCloseModals = () => {
      setIsRenumberingTransection(false);
   };

   const handleOpenBankReconciliation = () => {
      setIsRenumberingTransection(true);
   };

   const handleRenumberingTransection = () => {
      // Implementation for handling renumbering transaction
   };

   // Navigation handler
   const handleReportNavigation = (reportName) => {
      const routeMap = {
         Statement: '/admin/report/Statement',
         Outstanding: '/admin/report/Outstanding',
         'Profit & Loss': '/admin/report/ProfitLoss',
         'Balance Sheet': '/admin/report/BalanceSheet',
         'Trial Balance': '/admin/report/TrialBalance',
         Expenses: '/admin/report/Expenses',
         Income: '/admin/report/Income',
         'Purchase Detail': '/admin/report/PurchaseDetail',
         'Purchase Return Detail': '/admin/report/PurchaseReturnDetail',
         'Purchase Summary': '/admin/report/PurchaseSummary',
         'Purchase Return Summary': '/admin/report/PurchaseReturnSummary',
         'Sales Detail': '/admin/report/SalesDetail',
         'Sale Return Detail': '/admin/report/SaleReturnDetail',
         'Sale Summary': '/admin/report/SaleSummary',
         'Sale Return Summary': '/admin/report/SaleReturnSummary',
         'Transaction History': '/admin/report/TransactionHistory',
         Stock: '/admin/report/Stock',
         'Party Stock': '/admin/report/PartyStock',
         'All Transaction': '/admin/report/AllTransaction',
         SerialStock: '/admin/report/SerialStock',
         'Inventory Detail': '/admin/report/InventoryDetail',
         'Serial Wise Bill To Bill Profit': '/admin/report/SerialWiseBillToBillProfit',
         'Item + Serial wise History': '/admin/report/ItemSerialwiseHistory',
         'TDS REPORT': '/admin/report/TDSREPORT',
         GSTR1: '/admin/report/GSTR1',
         GSTR3B: '/admin/report/GSTR3B',
         'GSTR 2A/B2B RECONCILATION': user?.packageData?.[0]?.is2a2b ? '/admin/report/GSTR2AB2BReconcilation' : null,
      };

      const route = routeMap[reportName];
      if (route) {
         navigate(route);
      } else {
         console.warn(`No route defined for report: ${reportName}`);
      }
   };

   // Dynamic styles based on color mode
   const bgColor = isDark ? '#1a202c' : '#f7fafc';
   const cardBg = isDark ? '#2d3748' : '#ffffff';
   const textColor = isDark ? '#e2e8f0' : '#1a202c';
   const borderColor = isDark ? '#4a5568' : '#e2e8f0';
   const shadowColor = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)';
   const scrollbarThumb = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';

   // Enhanced color system with proper dark mode support
   const getColorClasses = (color) => {
      const colorMap = {
         blue: {
            gradient: 'from-blue-500 to-blue-600',
            bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
            text: isDark ? 'text-blue-300' : 'text-blue-600',
            border: isDark ? 'border-blue-700' : 'border-blue-200',
            activeBg: 'linear-gradient(135deg, #4299e1, #667eea)',
         },
         emerald: {
            gradient: 'from-emerald-500 to-emerald-600',
            bg: isDark ? 'bg-emerald-900/20' : 'bg-emerald-50',
            text: isDark ? 'text-emerald-300' : 'text-emerald-600',
            border: isDark ? 'border-emerald-700' : 'border-emerald-200',
            activeBg: 'linear-gradient(135deg, #48bb78, #38a169)',
         },
         violet: {
            gradient: 'from-violet-500 to-violet-600',
            bg: isDark ? 'bg-violet-900/20' : 'bg-violet-50',
            text: isDark ? 'text-violet-300' : 'text-violet-600',
            border: isDark ? 'border-violet-700' : 'border-violet-200',
            activeBg: 'linear-gradient(135deg, #9f7aea, #805ad5)',
         },
         amber: {
            gradient: 'from-amber-500 to-amber-600',
            bg: isDark ? 'bg-amber-900/20' : 'bg-amber-50',
            text: isDark ? 'text-amber-300' : 'text-amber-600',
            border: isDark ? 'border-amber-700' : 'border-amber-200',
            activeBg: 'linear-gradient(135deg, #ed8936, #dd6b20)',
         },
      };
      return colorMap[color] || colorMap.blue;
   };

   // GST reports array - conditionally included
   const gstReports = shouldShowGSTReports && user?.packageData?.[0]?.is2a2b
      ? [
         { name: 'GSTR1', image: img14, desc: 'Outward supplies return', frequency: 'Monthly' },
         { name: 'GSTR3B', image: img15, desc: 'Summary GST return', frequency: 'Monthly' },
         { name: 'GSTR 2A/B2B RECONCILATION', image: img15, desc: 'GST reconciliation', frequency: 'Monthly' },
      ]
      : shouldShowGSTReports ? [
         { name: 'GSTR1', image: img14, desc: 'Outward supplies return', frequency: 'Monthly' },
         { name: 'GSTR3B', image: img15, desc: 'Summary GST return', frequency: 'Monthly' },
      ]
         : [];

   // Module data structure
   const modules = {
      accounting: {
         title: 'Accounting',
         color: 'blue',
         items: [
            { name: 'Statement', image: img03, desc: 'View detailed account statements', frequency: 'Daily' },
            { name: 'Outstanding', image: img3, desc: 'Track pending receivables', frequency: 'Weekly' },
            { name: 'All Transaction', image: img18, desc: 'Complete inventory log', frequency: 'Daily' },
            {
               name: 'Profit & Loss',
               image: img1,
               desc: 'Income vs expenses analysis',
               frequency: 'Monthly',
               imageWidth: '100px',
               imageHeight: '100px',
            },
            { name: 'Balance Sheet', image: img2, desc: 'Assets, liabilities & equity', frequency: 'Monthly' },
            { name: 'Trial Balance', image: img2, desc: 'Verify accounting accuracy', frequency: 'Monthly' },
         ],
      },
      purchase: {
         title: 'Purchase & Sales',
         color: 'emerald',
         items: [
            { name: 'Purchase Detail', image: img6, desc: 'Complete purchase records', frequency: 'Daily' },
            { name: 'Purchase Return Detail', image: img6, desc: 'Return transactions', frequency: 'Weekly' },
            { name: 'Purchase Summary', image: img7, desc: 'Overview of all purchases', frequency: 'Monthly' },
            { name: 'Purchase Return Summary', image: img7, desc: 'Return summary', frequency: 'Monthly' },
            { name: 'Sales Detail', image: img8, desc: 'Complete sales records', frequency: 'Daily' },
            { name: 'Sale Return Detail', image: img8, desc: 'Handle customer returns', frequency: 'Weekly' },
            { name: 'Sale Summary', image: img9, desc: 'Comprehensive sales overview', frequency: 'Monthly' },
            { name: 'Sale Return Summary', image: img9, desc: 'Return analytics', frequency: 'Monthly' },
            {
               name: 'Bank Reconciliation',
               image: img10,
               desc: 'Match bank transactions',
               frequency: 'Monthly',
               isBankRecon: true,
            },
            { name: 'Transaction History', image: img9, desc: 'All financial transactions', frequency: 'Daily' },
         ],
      },
      inventory: {
         title: 'Inventory',
         color: 'violet',
         items: [
            { name: 'Stock', image: img11, desc: 'Current stock levels', frequency: 'Real-time' },
            ...(isMobileBusiness
               ? [{ name: 'Party Stock', image: img19, desc: 'Stock by party/vendor', frequency: 'Daily' }]
               : []),
            { name: 'SerialStock', image: img18, desc: 'Serial number tracking', frequency: 'Real-time' },
            { name: 'Inventory Detail', image: img12, desc: 'Detailed stock analysis', frequency: 'Weekly' },
            {
               name: 'Serial Wise Bill To Bill Profit',
               image: img17,
               desc: 'Serial-wise profitability',
               frequency: 'Monthly',
            },
            { name: 'Item + Serial wise History', image: img16, desc: 'Item movement tracking', frequency: 'Daily' },
         ],
      },
      tax: {
         title: 'Tax & Compliance',
         color: 'amber',
         items: [
            { name: 'TDS REPORT', image: img13, desc: 'Tax Deducted at Source', frequency: 'Quarterly' },
            ...gstReports,
         ],
      },
   };

   const currentModule = modules[activeTab];
   const filteredItems = currentModule.items.filter(
      (item) =>
         item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         item.desc.toLowerCase().includes(searchTerm.toLowerCase()),
   );

   // Don't render until initialized to prevent flash of wrong tab
   if (!isInitialized) {
      return (
         <div className="pt-9 h-[calc(100vh-80px)] flex items-center justify-center">
            <div className="text-center">
               <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
               <p className="mt-4 text-gray-500">Loading reports...</p>
            </div>
         </div>
      );
   }

   return (
      <>
         <div className="pt-14 h-[calc(100vh-80px)] relative overflow-hidden transition-colors duration-300">
            <div
               className="h-full overflow-y-auto px-2.5 md:px-5"
               style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${scrollbarThumb} transparent`,
               }}>
               <style jsx>{`
                  div::-webkit-scrollbar {
                     width: 6px;
                  }
                  div::-webkit-scrollbar-track {
                     background: transparent;
                  }
                  div::-webkit-scrollbar-thumb {
                     background: ${scrollbarThumb};
                     border-radius: 10px;
                  }
                  div::-webkit-scrollbar-thumb:hover {
                     background: ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
                  }
               `}</style>

               {/* Page Header */}
               {isLoading ? (
                  <HeaderSkeleton />
               ) : (
                  <div className="flex mb-3">
                     <div className="flex-1">
                        <h2 className="text-3xl font-bold mb-2" style={{ color: textColor }}>
                           Reports & Analytics
                        </h2>
                        <p className="text-gray-500">Access your financial reports and business insights</p>
                     </div>
                  </div>
               )}

               {/* Navigation Tabs */}
               {isLoading ? (
                  <TabSkeleton />
               ) : (
                  <div
                     className="rounded-2xl p-2 mb-6"
                     style={{
                        backgroundColor: cardBg,
                        boxShadow: `0 2px 4px ${shadowColor}`,
                        border: `1px solid ${borderColor}`,
                     }}>
                     <div className="grid grid-cols-4 gap-2">
                        {Object.entries(modules).map(([key, module]) => {
                           const isActive = activeTab === key;
                           const colors = getColorClasses(module.color);

                           return (
                              <button
                                 key={key}
                                 onClick={() => setActiveTab(key)}
                                 className={`px-4 py-3 rounded-xl transition-all font-semibold text-sm border-2 ${isActive
                                    ? 'text-white shadow-lg transform scale-[1.02]'
                                    : 'hover:scale-[1.01] hover:shadow-md'
                                    }`}
                                 style={{
                                    background: isActive ? colors.activeBg : 'transparent',
                                    color: isActive ? '#ffffff' : '#CBCBCB',
                                    borderColor: isActive ? colors.border : colors.border,
                                    borderWidth: '2px',
                                 }}>
                                 {module.title}
                              </button>
                           );
                        })}
                     </div>
                  </div>
               )}

               {/* Reports Grid */}
               {isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
                     {Array.from({ length: 8 }).map((_, index) => (
                        <ReportCardSkeleton key={index} />
                     ))}
                  </div>
               ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
                     {filteredItems.map((item, index) => {
                        const colors = getColorClasses(currentModule.color);

                        return (
                           <div
                              key={index}
                              className="group rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer border-2"
                              style={{
                                 backgroundColor: cardBg,
                                 boxShadow: `0 2px 4px ${shadowColor}`,
                                 borderColor: colors.border,
                              }}
                              onClick={() => {
                                 if (item.isBankRecon) {
                                    handleOpenBankReconciliation();
                                 } else {
                                    handleReportNavigation(item.name);
                                 }
                              }}>
                              <div className="p-6">
                                 {/* Header */}
                                 <div className="flex items-start mb-4">
                                    <div
                                       className="w-14 h-14 rounded-xl flex items-center justify-center p-2 border-2 mr-4"
                                       style={{
                                          backgroundColor: colors.bg,
                                          borderColor: colors.border,
                                       }}>
                                       <img
                                          src={item.image}
                                          alt={item.name}
                                          className="w-full h-full object-contain"
                                          style={{
                                             width: item.imageWidth || 'auto',
                                             height: item.imageHeight || 'auto',
                                          }}
                                       />
                                    </div>
                                    <div>
                                       <h3
                                          className="text-lg font-semibold mb-2 group-hover:text-blue-600 transition-colors"
                                          style={{ color: textColor }}>
                                          {item.name}
                                       </h3>
                                       <p className="text-sm text-gray-500 mb-2 line-clamp-2">{item.desc}</p>
                                    </div>
                                 </div>

                                 {/* Title & Description */}

                                 {/* Metadata */}
                                 <div
                                    className="flex items-center justify-between pt-4"
                                    style={{ borderTop: `1px solid ${borderColor}` }}>
                                    <div>
                                       {/* <p className="text-xs text-gray-500 mb-0.5">Frequency</p>
                                       <p className="text-sm font-medium" style={{ color: textColor }}>
                                          {item.frequency}
                                       </p> */}
                                    </div>
                                    <ChevronRight
                                       className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
                                       size={20}
                                    />
                                 </div>
                              </div>

                              {/* Hover Effect Bar */}
                              <div
                                 className={`h-1 bg-gradient-to-r ${colors.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left`}></div>
                           </div>
                        );
                     })}
                  </div>
               )}

               {/* Empty State */}
               {!isLoading && filteredItems.length === 0 && (
                  <div
                     className="rounded-2xl p-12 text-center"
                     style={{
                        backgroundColor: cardBg,
                        boxShadow: `0 2px 4px ${shadowColor}`,
                        border: `1px solid ${borderColor}`,
                     }}>
                     <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: isDark ? '#4a5568' : '#f7fafc' }}>
                        <Search className="text-gray-400" size={32} />
                     </div>
                     <h3 className="text-lg font-semibold mb-2" style={{ color: textColor }}>
                        No results found
                     </h3>
                     <p className="text-gray-500">Try adjusting your search terms</p>
                  </div>
               )}
            </div>
         </div>

         <BankReconciliation
            isRenumberingTransection={isRenumberingTransection}
            onClose={handleCloseModals}
            handleRenumberingTransection={handleRenumberingTransection}
         />
      </>
   );
}

// import React, { useState, useEffect, useLayoutEffect } from 'react';
// import { useColorMode } from '@chakra-ui/react';

// // Custom components
// import HistoryItem from 'views/admin/report/components/HistoryItem.js';
// import BankReconciliation from '../utility/components/BankReconciliation';

// // Assets
// import img1 from 'assets/img/nfts/profitloss.svg';
// import img2 from 'assets/img/nfts/balance sheet.svg';
// import img3 from 'assets/img/nfts/contacts payable.svg';
// import img03 from 'assets/img/nfts/statement.svg';
// import img4 from 'assets/img/nfts/expenses.svg';
// import img5 from 'assets/img/nfts/income.svg';
// import img6 from 'assets/img/nfts/purchase detail.svg';
// import img7 from 'assets/img/nfts/purchase summary.svg';
// import img8 from 'assets/img/nfts/sale detail.svg';
// import img9 from 'assets/img/nfts/sale summary.svg';
// import img10 from 'assets/img/nfts/item by contact.svg';
// import img11 from 'assets/img/nfts/stock.svg';
// import img12 from 'assets/img/nfts/retail.svg';
// import img13 from 'assets/img/nfts/tds report.svg';
// import img14 from 'assets/img/nfts/gstr1.svg';
// import img15 from 'assets/img/nfts/gstr3b.svg';
// import img16 from 'assets/img/nfts/Item + Serialwise History.svg';
// import img17 from 'assets/img/nfts/Serial Wise Bill To Bill Profit.svg';
// import img18 from 'assets/img/nfts/Serial Stock.svg';
// import img19 from 'assets/img/nfts/Party Stock.svg';

// export default function Marketplace() {
//    const { colorMode } = useColorMode();
//    const isDark = colorMode === 'dark';

//    const [isRenumberingTransection, setIsRenumberingTransection] = useState(false);
//    const [isMobileBusiness, setIsMobileBusiness] = useState(false);
//    const [mounted, setMounted] = useState(false);

//    // Use useLayoutEffect to ensure localStorage is read before first render
//    useLayoutEffect(() => {
//       const checkBusinessType = () => {
//          try {
//             if (typeof window !== 'undefined' && window.localStorage) {
//                const businessType = localStorage.getItem('businessTypes');
//                console.log('Reading businessType:', businessType);
//                setIsMobileBusiness(businessType === 'MOBILE' || businessType === 'mobile');
//                setMounted(true);
//                return true;
//             }
//          } catch (error) {
//             console.error('Error reading localStorage:', error);
//          }
//          return false;
//       };

//       if (!checkBusinessType()) {
//          const timer = setTimeout(() => {
//             checkBusinessType();
//          }, 50);
//          return () => clearTimeout(timer);
//       }
//    }, []);

//    // Additional useEffect for storage changes and page visibility
//    useEffect(() => {
//       if (!mounted) return;

//       const updateBusinessType = () => {
//          try {
//             const businessType = localStorage.getItem('businessTypes');
//             console.log('Updating businessType:', businessType);
//             setIsMobileBusiness(businessType === 'MOBILE' || businessType === 'mobile');
//          } catch (error) {
//             console.error('Error updating businessType:', error);
//          }
//       };

//       const handleStorageChange = (e) => {
//          if (e.key === 'businessTypes') {
//             console.log('Storage event fired:', e.newValue);
//             setIsMobileBusiness(e.newValue === 'MOBILE' || e.newValue === 'mobile');
//          }
//       };

//       const handleVisibilityChange = () => {
//          if (!document.hidden) {
//             updateBusinessType();
//          }
//       };

//       const handlePageShow = () => {
//          updateBusinessType();
//       };

//       window.addEventListener('storage', handleStorageChange);
//       document.addEventListener('visibilitychange', handleVisibilityChange);
//       window.addEventListener('pageshow', handlePageShow);
//       window.addEventListener('focus', updateBusinessType);

//       return () => {
//          window.removeEventListener('storage', handleStorageChange);
//          document.removeEventListener('visibilitychange', handleVisibilityChange);
//          window.removeEventListener('pageshow', handlePageShow);
//          window.removeEventListener('focus', updateBusinessType);
//       };
//    }, [mounted]);

//    const handleCloseModals = () => {
//       setIsRenumberingTransection(false);
//    };

//    const handleOpenBankReconciliation = () => {
//       setIsRenumberingTransection(true);
//    };

//    const handleRenumberingTransection = () => {
//       // Implementation for handling renumbering transaction
//    };

//    // Force re-check businessType on every render as a fallback
//    useEffect(() => {
//       const intervalId = setInterval(() => {
//          try {
//             const currentBusinessType = localStorage.getItem('businessTypes');
//             const shouldShowMobile = currentBusinessType === 'MOBILE' || currentBusinessType === 'mobile';
//             if (shouldShowMobile !== isMobileBusiness) {
//                console.log('Force update businessType:', currentBusinessType);
//                setIsMobileBusiness(shouldShowMobile);
//             }
//          } catch (error) {
//             // Silent fail
//          }
//       }, 1000);

//       return () => clearInterval(intervalId);
//    }, [isMobileBusiness]);

//    // Dynamic styles based on color mode
//    const bgColor = isDark ? '#1a202c' : '#f7fafc';
//    const cardBg = isDark ? '#2d3748' : '#ffffff';
//    const textColor = isDark ? '#e2e8f0' : '#1a202c';
//    const borderColor = isDark ? '#4a5568' : '#e2e8f0';
//    const hoverBg = isDark ? '#1a365d' : '#f7fafc';
//    const shadowColor = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)';
//    const headerBg = isDark ? '#2d3748' : '#ffffff';
//    const scrollbarThumb = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';

//    return (
//       <>
//          <div className="pt-12 h-[calc(100vh-80px)] relative overflow-hidden transition-colors duration-300">
//             <div
//                className="h-full overflow-y-auto px-2.5 md:px-5"
//                style={{
//                   scrollbarWidth: 'thin',
//                   scrollbarColor: `${scrollbarThumb} transparent`,
//                }}>
//                <style jsx>{`
//                   div::-webkit-scrollbar {
//                      width: 6px;
//                   }
//                   div::-webkit-scrollbar-track {
//                      background: transparent;
//                   }
//                   div::-webkit-scrollbar-thumb {
//                      background: ${scrollbarThumb};
//                      border-radius: 10px;
//                   }
//                   div::-webkit-scrollbar-thumb:hover {
//                      background: ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
//                   }
//                `}</style>

//                {/* Main Fields */}
//                <div className="mb-5 grid xl:grid-cols-3 gap-5">
//                   {/* Accounting Card */}
//                   <div
//                      className="rounded-2xl overflow-hidden transition-all duration-300 mb-5 xl:mb-0 hover:scale-[1.02]"
//                      style={{
//                         backgroundColor: cardBg,
//                         boxShadow: `0 4px 6px ${shadowColor}`,
//                      }}>
//                      <div
//                         className="flex items-center justify-between px-6 py-5"
//                         style={{
//                            backgroundColor: headerBg,
//                            borderBottom: `2px solid ${borderColor}`,
//                         }}>
//                         <h2
//                            className="text-lg xl:text-xl font-bold tracking-tight flex items-center gap-2"
//                            style={{ color: textColor }}>
//                            <span className="inline-block w-1 h-6 bg-blue-500 rounded-full"></span>
//                            Accounting
//                         </h2>
//                      </div>
//                      <div className="p-3">
//                         <HistoryItem name="Statement" image={img03} />
//                         <HistoryItem name="Outstanding" image={img3} />
//                         <HistoryItem name="Profit & Loss" image={img1} imageWidth="100px" imageHeight="100px" />
//                         <HistoryItem name="Balance Sheet" image={img2} />
//                         <HistoryItem name="Trial Balance" image={img2} />
//                      </div>
//                   </div>

//                   {/* Purchase/Sale Card */}
//                   <div
//                      className="rounded-2xl overflow-hidden transition-all duration-300 mb-5 xl:mb-0 hover:scale-[1.02]"
//                      style={{
//                         backgroundColor: cardBg,
//                         boxShadow: `0 4px 6px ${shadowColor}`,
//                      }}>
//                      <div
//                         className="flex items-center justify-between px-6 py-5"
//                         style={{
//                            backgroundColor: headerBg,
//                            borderBottom: `2px solid ${borderColor}`,
//                         }}>
//                         <h2
//                            className="text-lg xl:text-xl font-bold tracking-tight flex items-center gap-2"
//                            style={{ color: textColor }}>
//                            <span className="inline-block w-1 h-6 bg-green-500 rounded-full"></span>
//                            Purchase/Sale
//                         </h2>
//                      </div>
//                      <div className="p-3">
//                         <HistoryItem name="Purchase Detail" image={img6} />
//                         <HistoryItem name="Purchase Return Detail" image={img6} />
//                         <HistoryItem name="Purchase Summary" image={img7} />
//                         <HistoryItem name="Purchase Return Summary" image={img7} />
//                         <HistoryItem name="Sales Detail" image={img8} />
//                         <HistoryItem name="Sale Return Detail" image={img8} />
//                         <HistoryItem name="Sale Summary" image={img9} />
//                         <HistoryItem name="Sale Return Summary" image={img9} />

//                         {/* Bank Reconciliation */}
//                         <div className="px-2 py-1">
//                            <div
//                               className="rounded-xl cursor-pointer transition-all duration-200 transform hover:scale-[1.02]"
//                               style={{
//                                  border: `1px solid ${borderColor}`,
//                                  backgroundColor: cardBg,
//                               }}
//                               onClick={handleOpenBankReconciliation}
//                               onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
//                               onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = cardBg)}>
//                               <div className="flex items-center p-4">
//                                  <div className="w-11 h-11 mr-4 flex-shrink-0 rounded-lg bg-gradient-to-br from-purple-400 to-blue-500 p-2 shadow-md">
//                                     <img src={img10} alt="" className="w-full h-full object-contain" />
//                                  </div>
//                                  <span className="text-sm font-semibold" style={{ color: textColor }}>
//                                     Bank Reconciliation
//                                  </span>
//                               </div>
//                            </div>
//                         </div>

//                         <HistoryItem name="Transaction History" image={img9} />
//                      </div>
//                   </div>

//                   {/* Inventory & Tax Column */}
//                   <div className="flex flex-col space-y-5">
//                      {/* Inventory Card */}
//                      <div
//                         className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
//                         style={{
//                            backgroundColor: cardBg,
//                            boxShadow: `0 4px 6px ${shadowColor}`,
//                         }}>
//                         <div
//                            className="flex items-center justify-between px-6 py-5"
//                            style={{
//                               backgroundColor: headerBg,
//                               borderBottom: `2px solid ${borderColor}`,
//                            }}>
//                            <h2
//                               className="text-lg xl:text-xl font-bold tracking-tight flex items-center gap-2"
//                               style={{ color: textColor }}>
//                               <span className="inline-block w-1 h-6 bg-purple-500 rounded-full"></span>
//                               Inventory
//                            </h2>
//                         </div>
//                         <div className="p-3">
//                            <HistoryItem name="Stock" image={img11} />
//                            {isMobileBusiness && <HistoryItem name="Party Stock" image={img19} />}
//                            <HistoryItem name="All Transaction" image={img18} />
//                            <HistoryItem name="SerialStock" image={img18} />
//                            <HistoryItem name="Inventory Detail" image={img12} />
//                            <HistoryItem name="Serial Wise Bill To Bill Profit" image={img17} />
//                            <HistoryItem name="Item + Serial wise History" image={img16} />
//                         </div>
//                      </div>

//                      {/* Tax Card */}
//                      <div
//                         className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
//                         style={{
//                            backgroundColor: cardBg,
//                            boxShadow: `0 4px 6px ${shadowColor}`,
//                         }}>
//                         <div
//                            className="flex items-center justify-between px-6 py-5"
//                            style={{
//                               backgroundColor: headerBg,
//                               borderBottom: `2px solid ${borderColor}`,
//                            }}>
//                            <h2
//                               className="text-lg xl:text-xl font-bold tracking-tight flex items-center gap-2"
//                               style={{ color: textColor }}>
//                               <span className="inline-block w-1 h-6 bg-orange-500 rounded-full"></span>
//                               Tax
//                            </h2>
//                         </div>
//                         <div className="p-3">
//                            <HistoryItem name="TDS REPORT" image={img13} />
//                            <HistoryItem name="GSTR1" image={img14} />
//                            <HistoryItem name="GSTR3B" image={img15} />
//                            <HistoryItem name="GSTR 2A/B2B RECONCILATION" image={img15} />
//                         </div>
//                      </div>
//                   </div>
//                </div>
//             </div>
//          </div>

//          <BankReconciliation
//             isRenumberingTransection={isRenumberingTransection}
//             onClose={handleCloseModals}
//             handleRenumberingTransection={handleRenumberingTransection}
//          />
//       </>
//    );
// }
